import { SELF_HARM_REPLY, checkInput, cleanReply } from "./guard.js";
import type { Grounding } from "./grounding.js";
import { validGuide, type ChatResponse, type ChatTurn } from "./prompt.js";

const WORDS: Record<string, string> = { ground: "G", g: "G", one: "1", first: "1", two: "2", second: "2", three: "3", third: "3", four: "4", fourth: "4", five: "5", fifth: "5", six: "6", sixth: "6", seven: "7", seventh: "7" };
const STOP = new Set("what whats there level floor about tell would could should please thank thanks hello have this that with your from they them then than here where which who when does want like some more much very just also into over only know need".split(" "));

type Place = { locationId: string; badge: string; label: string; facts: string[] };

/** Places a visitor can name: guide places that are not lift floors first (e.g. a heritage room), then the lift floors. */
function placesOf(g: Grounding): Place[] {
  const extra = g.guides.filter((y) => !g.floors.some((f) => f.locationId === y.locationId)).map((y) => ({ locationId: y.locationId, badge: y.badge ?? "", label: y.label, facts: [] as string[] }));
  return [...extra, ...g.floors];
}

/** Match a floor by number ("7", "seven", "level 7") or by name words ("mclaren", "terrace"). */
export function matchFloor(g: Grounding, text: string): string | undefined {
  const t = text.toLowerCase();
  const places = placesOf(g);
  const num = /\b(?:floor|level)?\s*(\d|g)\b/.exec(t)?.[1]?.toUpperCase() ?? Object.entries(WORDS).find(([w]) => new RegExp(`\\b(?:floor|level) ${w}\\b|\\b${w} (?:floor|please)\\b`).test(t))?.[1];
  if (num) {
    const f = places.find((x) => x.badge.toUpperCase() === num);
    if (f) return f.locationId;
  }
  // Best name match wins (most label words in the text), so "racing room" beats any other "... room"; ties keep the
  // shorter label first (bare "mclaren" -> the McLaren floor), then list order.
  let best: { id: string; hits: number; len: number } | undefined;
  for (const f of places) {
    const words = f.label.toLowerCase().replace(/^the /, "").split(/[^a-z]+/).filter((w) => w.length > 3);
    const hits = words.filter((w) => t.includes(w)).length;
    if (hits > 0 && (!best || hits > best.hits || (hits === best.hits && words.length < best.len))) best = { id: f.locationId, hits, len: words.length };
  }
  if (best) return best.id;
  if (/\b(cars?|supercars?|heritage|senna|f1)\b/.test(t)) return places.find((f) => /mclaren/i.test(f.label))?.locationId;
  if (/\b(lunch|dinner|eat|food|restaurant)\b/.test(t)) return places.find((f) => /orfeo|terrace/i.test(f.label))?.locationId;
  return undefined;
}

const sentences = (s: string) => s.trim().split(/(?<=[.!?]["')\]]*)\s+/);
const firstSentence = (s: string) => sentences(s)[0] ?? s;

/** Best-matching sentences from the character's own facts for a free-text question, or undefined. */
export function retrieveFact(g: Grounding, text: string): string | undefined {
  const qw = text.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3 && !STOP.has(w));
  if (!qw.length) return undefined;
  let best: { score: number; len: number; sents: string[] } | undefined;
  for (const fact of g.facts) {
    const ss = sentences(fact);
    const score = qw.filter((w) => fact.toLowerCase().includes(w.replace(/s$/, ""))).length;
    if (score > 0 && (!best || score > best.score || (score === best.score && fact.length > best.len))) {
      // Prefer the sentence(s) with the most hits, then the one after it.
      const idx = ss.map((s) => qw.filter((w) => s.toLowerCase().includes(w.replace(/s$/, ""))).length);
      const top = idx.indexOf(Math.max(...idx));
      best = { score, len: fact.length, sents: ss.slice(top, top + 2) };
    }
  }
  return best?.sents.join(" ");
}

/**
 * Offline stand-in for the LLM: the same input guard as the worker, then keyword rules over the same grounding.
 * Lets the chat UI, travel action and voice be demoed with no backend, with sensible per-character answers.
 */
export function mockReply(g: Grounding, history: ChatTurn[]): ChatResponse {
  const reply = (text: string, travelTo?: string): ChatResponse => ({ reply: cleanReply(text, g.rules.deflection).text, ...(travelTo ? { travelTo } : {}) });
  const last = [...history].reverse().find((m) => m.role === "user")?.content ?? "";
  const t = last.toLowerCase();
  const verdict = checkInput(last);
  if (!verdict.ok) return { reply: verdict.reason === "self-harm" ? SELF_HARM_REPLY : g.rules.deflection };
  const floor = matchFloor(g, t);
  const f = placesOf(g).find((x) => x.locationId === floor);
  const wantsGo = /\b(take|go|going|bring|travel|up|down|press|let'?s|please|guide|lead|show me the way)\b/.test(t);
  const canGo = floor ? validGuide(g, floor) : undefined;
  const where = (x: { badge: string; label: string }) => (x.badge ? `${x.label}, on ${x.badge === "G" ? "the ground floor" : `level ${x.badge}`}` : x.label);
  if (f && canGo && wantsGo) return reply(`${where(f)}. Right this way.`, canGo);
  if (f) {
    // Pick the fact sharing most words with the question (e.g. "heritage" -> the gallery plaque).
    const qw = t.split(/[^a-z0-9]+/).filter((w) => w.length > 3 && !STOP.has(w));
    const score = (x: string) => qw.filter((w) => x.toLowerCase().includes(w)).length;
    const fact = [...f.facts].sort((a, b) => score(b) - score(a))[0] ?? "";
    const tail = canGo ? " Shall I take you there?" : " The lift will take you.";
    return reply(`${where(f)}. ${firstSentence(fact)}${tail}`);
  }
  if (/\b(hello|hi|good (morning|afternoon|evening)|hey|bonjour|buonasera)\b/.test(t)) return reply(g.rules.greeting);
  if (/\b(thanks|thank you|cheers|bye|goodbye)\b/.test(t)) return reply("My pleasure. Enjoy your visit.");
  const fact = retrieveFact(g, t);
  if (fact) return reply(fact);
  if (g.floors.length && /\b(floors?|where|what'?s (up|there)|options|levels?)\b/.test(t)) {
    const lo = g.floors[0];
    const hi = g.floors.at(-1);
    return reply(`The lift serves ${g.floors.length} floors, from ${lo?.label ?? "the ground floor"} up to ${hi?.label ?? "the top"}${hi?.badge ? ` on level ${hi.badge}` : ""}.${g.guides.length ? " Just name one." : ""}`);
  }
  const topic = g.rules.knows[0] ?? "this place";
  return reply(`I'm afraid I couldn't say. I can tell you about ${topic}${g.guides.length ? ", or take you somewhere" : ""}, though.`);
}
